import {
  DATE_FORMAT_OPTIONS,
  type DateFormat,
  DECIMAL_SEPARATOR_OPTIONS,
  type DecimalSeparator,
  decimalSeparatorChar,
  ENABLED_LOCALES,
  formatZonedDateTime,
  isLocaleEnabled,
  isValidTimeZone,
  LOCALE_NAMES,
  LOCALE_NATIVE_NAMES,
  regionalFormatSample,
  SUPPORTED_LOCALES,
  THOUSAND_SEPARATOR_OPTIONS,
  type ThousandSeparator,
  thousandSeparatorChar,
} from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { TENANT_CURRENCIES, type TenantCurrency } from "@repo/shared/types";
import { Info } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRegionalSettings, useUpdateRegionalSettings } from "@/lib/hooks/use-clients";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./regional-settings.i18n";

export function RegionalSettingsWrapper() {
  const t = useT(T);
  const { settings: serverSettings, isLoading } = useRegionalSettings();
  const updateSettings = useUpdateRegionalSettings();
  const [defaultLanguage, setDefaultLanguage] = useState<string | null>(null);
  const [supportedLanguages, setSupportedLanguages] = useState<string[] | null>(null);
  const [dateFormat, setDateFormat] = useState<DateFormat | null>(null);
  const [decimalSeparator, setDecimalSeparator] = useState<DecimalSeparator | null>(null);
  const [thousandSeparator, setThousandSeparator] = useState<ThousandSeparator | null>(null);
  const [currency, setCurrency] = useState<TenantCurrency | null>(null);
  const [timeZone, setTimeZone] = useState<string | null>(null);

  // Use local edits if present, otherwise server data
  const currentDefault = defaultLanguage ?? serverSettings.defaultLanguage;
  const currentSupported = supportedLanguages ?? serverSettings.supportedLanguages;
  const currentDateFormat = dateFormat ?? serverSettings.dateFormat;
  const currentDecimal = decimalSeparator ?? serverSettings.decimalSeparator;
  const currentThousand = thousandSeparator ?? serverSettings.thousandSeparator;
  const currentCurrency = currency ?? serverSettings.currency;
  const currentTimeZone = timeZone ?? serverSettings.timeZone;

  // QL-6: only enabled locales can be picked as default. If a tenant still has a
  // legacy default outside that list, keep it in the options so the select renders it.
  const defaultLanguageOptions: LocaleCode[] = isLocaleEnabled(currentDefault)
    ? [...ENABLED_LOCALES]
    : [...ENABLED_LOCALES, currentDefault as LocaleCode];

  const sample = regionalFormatSample({
    dateFormat: currentDateFormat,
    decimalSeparator: currentDecimal,
    thousandSeparator: currentThousand,
    timeZone: currentTimeZone,
  });

  // QL-689 — the browser's Intl database; a stored zone it does not know
  // (legacy data, a renamed IANA zone) is kept in the list so the select can
  // still render it, the same tolerance the legacy default-language case above gets.
  const timeZoneOptions = useMemo(() => {
    const supported =
      typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
    const zones = supported.includes(currentTimeZone) ? supported : [currentTimeZone, ...supported];
    return zones.map((zone) => ({ value: zone, label: zone }));
  }, [currentTimeZone]);

  const hasChanges =
    (defaultLanguage !== null && defaultLanguage !== serverSettings.defaultLanguage) ||
    (supportedLanguages !== null &&
      JSON.stringify([...supportedLanguages].sort()) !==
        JSON.stringify([...serverSettings.supportedLanguages].sort())) ||
    (dateFormat !== null && dateFormat !== serverSettings.dateFormat) ||
    (decimalSeparator !== null && decimalSeparator !== serverSettings.decimalSeparator) ||
    (thousandSeparator !== null && thousandSeparator !== serverSettings.thousandSeparator) ||
    (currency !== null && currency !== serverSettings.currency) ||
    (timeZone !== null && timeZone !== serverSettings.timeZone);

  const handleToggleLanguage = (locale: string) => {
    const current = supportedLanguages ?? [...serverSettings.supportedLanguages];
    if (current.includes(locale)) {
      if (current.length === 1) return;
      if (locale === currentDefault) return;
      setSupportedLanguages(current.filter((l) => l !== locale));
    } else {
      setSupportedLanguages([...current, locale]);
    }
  };

  const handleDefaultChange = (locale: string) => {
    setDefaultLanguage(locale);
    const current = supportedLanguages ?? [...serverSettings.supportedLanguages];
    if (!current.includes(locale)) {
      setSupportedLanguages([...current, locale]);
    }
  };

  const handleSave = () => {
    updateSettings.mutate(
      {
        defaultLanguage: currentDefault,
        supportedLanguages: currentSupported,
        dateFormat: currentDateFormat,
        decimalSeparator: currentDecimal,
        thousandSeparator: currentThousand,
        currency: currentCurrency,
        timeZone: currentTimeZone,
      },
      {
        onSuccess: () => {
          toast.success(t.saved);
          setDefaultLanguage(null);
          setSupportedLanguages(null);
          setDateFormat(null);
          setDecimalSeparator(null);
          setThousandSeparator(null);
          setCurrency(null);
          setTimeZone(null);
        },
        onError: (err: any) =>
          toast.error(t.error, { description: err?.message || t.failedToSave }),
      },
    );
  };

  if (isLoading) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.regional }]}
        title={t.regional}
        description={t.description}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t.formats}</CardTitle>
          <CardDescription>{t.formatsDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="date-format">{t.dateFormat}</Label>
              <Select
                value={currentDateFormat}
                onValueChange={(value) => setDateFormat(value as DateFormat)}
              >
                <SelectTrigger id="date-format" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FORMAT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.value}
                      <span className="text-muted-foreground ml-2">({option.sample})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="decimal-separator">{t.decimalSeparator}</Label>
              <Select
                value={currentDecimal}
                onValueChange={(value) => setDecimalSeparator(value as DecimalSeparator)}
              >
                <SelectTrigger id="decimal-separator" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DECIMAL_SEPARATOR_OPTIONS.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={
                        decimalSeparatorChar(option.value) ===
                        thousandSeparatorChar(currentThousand)
                      }
                    >
                      {t.separatorNames[option.value]}
                      <span className="text-muted-foreground ml-2">({option.sample})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="thousand-separator">{t.thousandSeparator}</Label>
              <Select
                value={currentThousand}
                onValueChange={(value) => setThousandSeparator(value as ThousandSeparator)}
              >
                <SelectTrigger id="thousand-separator" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {THOUSAND_SEPARATOR_OPTIONS.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={
                        thousandSeparatorChar(option.value) === decimalSeparatorChar(currentDecimal)
                      }
                    >
                      {t.separatorNames[option.value]}
                      <span className="text-muted-foreground ml-2">({option.sample})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-md border bg-muted/50 px-4 py-3 text-sm">
            <span className="text-muted-foreground mr-2">{t.preview}:</span>
            <span className="font-medium tabular-nums">
              {sample.date} · {sample.number}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.timeZone}</CardTitle>
          <CardDescription>{t.timeZoneDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <SearchableSelect
            id="time-zone"
            className="w-[280px]"
            value={currentTimeZone}
            onValueChange={setTimeZone}
            options={timeZoneOptions}
            searchPlaceholder={t.timeZoneSearchPlaceholder}
            noResultsMessage={t.timeZoneNoResults}
          />
          {/* A tolerated stored zone this browser's Intl rejects would throw a
              RangeError in formatZonedDateTime — skip the preview instead. */}
          {isValidTimeZone(currentTimeZone) && (
            <p className="text-sm text-muted-foreground">
              {t.localTimeNow}:{" "}
              {formatZonedDateTime(new Date(), {
                dateFormat: currentDateFormat,
                decimalSeparator: currentDecimal,
                thousandSeparator: currentThousand,
                timeZone: currentTimeZone,
              })}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.currency}</CardTitle>
          <CardDescription>{t.currencyDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={currentCurrency}
            onValueChange={(value) => setCurrency(value as TenantCurrency)}
          >
            <SelectTrigger id="currency" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TENANT_CURRENCIES.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.defaultLanguage}</CardTitle>
          <CardDescription>{t.defaultLanguageDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={currentDefault} onValueChange={handleDefaultChange}>
            <SelectTrigger className="w-[280px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {defaultLanguageOptions.map((locale) => (
                <SelectItem key={locale} value={locale}>
                  {LOCALE_NAMES[locale]} ({LOCALE_NATIVE_NAMES[locale]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ComingSoonNote text={t.comingSoon} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.supportedLanguages}</CardTitle>
          <CardDescription>{t.supportedLanguagesDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SUPPORTED_LOCALES.map((locale) => {
              const isDefault = locale === currentDefault;
              const isChecked = currentSupported.includes(locale);
              // QL-6: languages not enabled yet are shown but cannot be turned on.
              // A legacy tenant that already has one checked can still turn it off.
              const isComingSoon = !isLocaleEnabled(locale) && !isChecked;
              const isDisabled =
                isComingSoon || isDefault || (isChecked && currentSupported.length === 1);

              return (
                <div key={locale} className="flex items-center space-x-3">
                  <Checkbox
                    id={locale}
                    checked={isChecked}
                    onCheckedChange={() => handleToggleLanguage(locale)}
                    disabled={isDisabled}
                  />
                  <Label
                    htmlFor={locale}
                    className={`flex-1 ${isDisabled ? "cursor-not-allowed text-muted-foreground" : "cursor-pointer"}`}
                  >
                    <span className="font-medium">{LOCALE_NAMES[locale]}</span>
                    <span className="text-muted-foreground ml-1">
                      ({LOCALE_NATIVE_NAMES[locale]})
                    </span>
                    {isDefault && (
                      <span className="text-xs text-muted-foreground ml-2">({t.default})</span>
                    )}
                  </Label>
                </div>
              );
            })}
          </div>
          <ComingSoonNote text={t.comingSoon} />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={!hasChanges} loading={updateSettings.isPending}>
          {t.save}
        </Button>
      </div>
    </div>
  );
}

function ComingSoonNote({ text }: { text: string }) {
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Info className="size-4 shrink-0" aria-hidden="true" />
      {text}
    </p>
  );
}
